"use client"
import { useEffect, useState } from "react"
import styled, { keyframes } from "styled-components"
import { motion, useAnimation } from "framer-motion"
import { Link } from "react-router-dom"
import { FiGithub, FiMail, FiLinkedin, FiArrowDown, FiPlay } from "react-icons/fi"
import { ReactTyped } from "react-typed"
import CountUp from "react-countup"
import { InView } from "react-intersection-observer"
import { useApp } from "../../contexts/AppContext"
import ResponsiveContainer from "../ResponsiveContainer"
import ResponsiveGrid from "../ResponsiveGrid"
// Keyframes for animations
const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.6; }
  50% { transform: scale(1.05); opacity: 0.4; }
`
const shimmer = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(100%); }
`
const ping = keyframes`
  75%, 100% { transform: scale(2); opacity: 0; }
`
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`
const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
  60% { transform: translateY(-5px); }
`
const underlineExpand = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`
// Main Component Styles
const HeroSection = styled.section`
min-height: 100vh;
display: flex;
align-items: center;
justify-content: center;
position: relative;
overflow: hidden;
padding-top: 80px;
background: gradient(135deg, #F4F1DE,rgb(127, 169, 185)); 
@media (max-width: 768px) {
min-height: 100svh;
padding-top: 60px;
}
`
const HeroBackground = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  z-index: 1;
`
const BackgroundElement = styled(motion.div)`
position: absolute;
border-radius: 50%;
filter: blur(150px); // Increased blur for softer effect on light bg
opacity: 0.1;
@media (max-width: 768px) {
filter: blur(100px);
opacity: 0.12;
}
`
const FloatingElements = styled.div`
position: absolute;
inset: 0;
overflow: hidden;
z-index: 1;
@media (max-width: 640px) {
display: none;
}
`
const FloatingShape = styled(motion.div)`
  position: absolute;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: linear-gradient(45deg, #264653, #2A9D8F); // Gradient from new palette
  opacity: 0.08;
  animation: ${float} ${({ $duration }) => $duration}s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}s;
`
const HeroContent = styled(motion.div)`
  text-align: center;
  position: relative;
  z-index: 2;
  width: 100%;
`;
const ProfileImageContainer = styled(motion.div)`
margin-bottom: 2rem;
position: relative;
display: inline-block;
@media (max-width: 640px) {
margin-bottom: 1.5rem;
}
`
const ProfileImage = styled(motion.div)`
width: 250px;
height: 250px;
margin: 0 auto;
border-radius: 50%;
background: linear-gradient(45deg, #264653, #2A9D8F,rgb(23, 233, 208)); // New palette gradient
padding: 6px;
position: relative;
cursor: none;
&::before {
content: '';
position: absolute;
inset: -6px;
border-radius: 50%;
background: linear-gradient(45deg, #2A9D8F,  #2A9D8F);
z-index: -1;
filter: blur(20px);
opacity: 0.6;
animation: ${pulse} 3s infinite;
}
&::after {
content: '';
position: absolute;
inset: -12px;
border-radius: 50%;
background: linear-gradient(45deg,  #2A9D8F, #264653);
z-index: -2;
filter: blur(30px);
opacity: 0.4;
animation: ${pulse} 4s infinite reverse;
}
@media (max-width: 1024px) {
width: 220px;
height: 220px;
}
@media (max-width: 768px) {
width: 200px;
height: 200px;
}
@media (max-width: 640px) {
width: 180px;
height: 180px;
}
`
const ProfileImageInner = styled.div`
width: 100%;
height: 100%;
border-radius: 50%;
background: #FFFFFF; // Contrasting white background
display: flex;
align-items: center;
justify-content: center;
font-size: 6rem;
font-weight: 900;
color: #264653; // New Primary color
font-family: 'Poppins', sans-serif;
text-shadow: 0 2px 10px rgba(38, 70, 83, 0.2); // Softer shadow
position: relative;
overflow: hidden;
&::before {
content: '';
position: absolute;
top: 0;
left: 0;
width: 200%;
height: 100%;
background: linear-gradient(90deg, transparent,rgb(2, 78, 69), transparent); // Accent color shimmer
animation: ${shimmer} 3s infinite;
}
@media (max-width: 1024px) { font-size: 5rem; }
@media (max-width: 768px) { font-size: 4rem; }
@media (max-width: 640px) { font-size: 3rem; }
`
const StatusIndicator = styled(motion.div)`
position: absolute;
top: 10px;
right: 10px;
width: 30px;
height: 30px;
background: #2A9D8F; // New Secondary (Teal)
border-radius: 50%;
border: 4px solid #F4F1DE; // Main background color
box-shadow: 0 0 20px rgba(42, 157, 143, 0.5); // Glow matching Teal
&::after {
content: '';
position: absolute;
inset: 0;
border-radius: 50%;
background-color: #2A9D8F;
animation: ${ping} 1.5s infinite;
transform-origin: center;
}
@media (max-width: 640px) {
width: 24px;
height: 24px;
}
`
const Greeting = styled(motion.div)`
margin-bottom: 1rem;
span {
font-size: 1.5rem;
color: #2A9D8F; // New Secondary (Teal)
font-weight: 700;
letter-spacing: 4px;
text-transform: uppercase;
text-shadow: 1px 1px 2px rgba(0,0,0,0.1); // Cleaner shadow

@media (max-width: 768px) { font-size: 1.25rem; }
@media (max-width: 640px) { font-size: 1rem; }

}
`
const HeroTitle = styled(motion.h1)`
font-size: clamp(2.5rem, 8vw, 8rem);
font-weight: 900;
margin-bottom: 1.5rem;
font-family: 'Poppins', sans-serif;
line-height: 0.9;
position: relative;
color: #1B1B1E; // New Text color
.intro {
display: block;
font-size: clamp(1.5rem, 4vw, 3rem);
font-weight: 400;
margin-bottom: 0.5rem;
}
.name {

background: linear-gradient(90deg, #000,rgb(2, 90, 80), #264653);
-webkit-background-clip: text;
-webkit-text-fill-color: transparent;
background-clip: text;
position: relative;
display: inline-block;

&::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 0;
  width: 80%;
  height: 4px;
  background: linear-gradient(90deg, #264653, #2A9D8F); // Harmonious gradient
  border-radius: 1px;
  transform: scaleX(0);
  animation: ${underlineExpand} 2s ease-out 1s forwards;
  
  @media (max-width: 640px) {
    height: 2px;
    bottom: -5px;
  }
}

}
`
const HeroSubtitle = styled(motion.div)`
font-size: clamp(1.25rem, 4vw, 3rem);
font-weight: 600;
margin-bottom: 2rem;
color: #1B1B1E; // New Text color
position: relative;
.typed-text {
color: #264653; // New Primary color
font-weight: 700;
}
@media (max-width: 640px) { margin-bottom: 1.5rem; }
`
const HeroDescription = styled(motion.p)`
font-size: clamp(1rem, 2.5vw, 1.25rem);
color: #1B1B1E; // New text color
opacity: 0.9; // For hierarchy
margin-bottom: 2.5rem;
max-width: 900px;
margin-left: auto;
margin-right: auto;
line-height: 1.8;
font-weight: 500;
@media (max-width: 640px) {
margin-bottom: 2rem;
line-height: 1.6;
}
`
const CTAButtons = styled(motion.div)`
display: flex;
flex-direction: column;
gap: 1.5rem;
align-items: center;
margin-bottom: 2.5rem;
@media (min-width: 640px) {
flex-direction: row;
justify-content: center;
}
`
const PrimaryButton = styled(motion.div)`
position: relative;
border-radius: 9999px;
width: 100%;
max-width: 280px;
a {
display: inline-flex;
align-items: center;
justify-content: center;
gap: 0.5rem;
padding: 1rem 2.5rem;
background: #264653; // New Primary color
color: #F4F1DE; // New Background color for text
font-weight: 700;
border-radius: 9999px;
text-decoration: none;
font-size: clamp(1rem, 2.5vw, 1.25rem);
letter-spacing: 1px;
text-transform: uppercase;
transition: all 0.3s ease;
box-shadow: 0 4px 15px rgba(38, 70, 83, 0.3);
width: 100%;
min-height: 56px;

&:hover {
  background: #2A9D8F; // Hover with Secondary color
  transform: translateY(-3px);
  box-shadow: 0 7px 20px rgba(42, 157, 143, 0.3);
}

}
@media (min-width: 640px) { width: auto; }
`
const SecondaryButton = styled(motion.div)`
position: relative;
width: 100%;
max-width: 280px;
a {
display: inline-flex;
align-items: center;
justify-content: center;
gap: 0.5rem;
padding: 1rem 2.5rem;
background: transparent;
color: #264653; // New Primary color
font-weight: 700;
border: 2px solid #264653;
border-radius: 9999px;
text-decoration: none;
font-size: clamp(1rem, 2.5vw, 1.25rem);
letter-spacing: 1px;
text-transform: uppercase;
transition: all 0.3s ease;
width: 100%;
min-height: 56px;

&:hover {
  background: #264653;
  color:rgb(12, 12, 12);
  transform: translateY(-3px);
  box-shadow: 0 7px 20px rgba(38, 70, 83, 0.2);
}

}
@media (min-width: 640px) { width: auto; }
`
const SocialLinks = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-bottom: 3rem;
`;
const SocialLink = styled(motion.a)`
display: flex;
align-items: center;
justify-content: center;
width: 70px;
height: 70px;
background: transparent;
border: 2px solid rgba(38, 70, 83, 0.4); // Subtle border
border-radius: 50%;
color: #264653; // New Primary color
transition: all 0.3s ease;
text-decoration: none;
&:hover {
color:rgb(46, 46, 51);
background: #264653;
border-color: #264653;
transform: translateY(-8px);
box-shadow: 0 10px 20px rgba(38, 70, 83, 0.2);
}
@media (max-width: 640px) {
width: 60px;
height: 60px;
}
`
const StatsContainer = styled(motion.div)`
  width: 100%;
  margin: 0 auto;
`;
const StatItem = styled(motion.div)`
text-align: center;
padding: 2rem;
background: rgba(255, 255, 255, 0.6); // Semi-transparent white card
backdrop-filter: blur(10px);
border: 1px solid rgba(38, 70, 83, 0.15); // Subtle border
border-radius: 1.5rem;
transition: all 0.3s ease;
position: relative;
overflow: hidden;
&::before {
content: '';
position: absolute;
top: 0;
left: -100%;
width: 100%;
height: 100%;
background: linear-gradient(90deg, transparent,  #2A9D8F, transparent); // Accent color shimmer
transition: left 0.8s ease;
}
&:hover {
transform: translateY(-8px);
border-color: #2A9D8F; // Hover with Secondary color
box-shadow: 0 10px 25px rgba(42, 157, 143, 0.15);

&::before {
  left: 100%;
}

}
.number {
font-size: clamp(2rem, 6vw, 5rem);
font-weight: 900;
color: #264653; // New Primary color
margin-bottom: 0.5rem;
font-family: 'Poppins', sans-serif;
}
.label {
color: #1B1B1E;
opacity: 0.8;
font-size: clamp(0.875rem, 2vw, 1.125rem);
font-weight: 500;
text-transform: uppercase;
letter-spacing: 1px;
}
`
const ScrollIndicator = styled(motion.div)`
position: absolute;
bottom: 2rem;
left: 50%;
transform: translateX(-50%);
display: flex;
flex-direction: column;
align-items: center;
gap: 0.5rem;
color: rgba(38, 70, 83, 0.7); // Muted Primary color
cursor: pointer;
span {
font-size: 0.75rem;
text-transform: uppercase;
letter-spacing: 2px;
font-weight: 500;
}
svg {
animation: ${bounce} 2s infinite;
}
&:hover {
color: #264653; // Solid Primary on hover
}
`
const EnhancedHero = () => {
const { mousePosition } = useApp()
const [isVisible, setIsVisible] = useState(false)
const controls = useAnimation()
useEffect(() => {
controls.start("visible")
}, [controls])
const containerVariants = {
hidden: { opacity: 0 },
visible: {
opacity: 1,
transition: {
delayChildren: 0.5,
staggerChildren: 0.3,
},
},
}
const itemVariants = {
hidden: { y: 50, opacity: 0 },
visible: {
y: 0,
opacity: 1,
transition: {
duration: 0.8,
type: "spring",
stiffness: 100,
},
},
}
const floatingShapes = Array.from({ length: 6 }, (_, i) => ({
size: Math.random() * 80 + 40,
duration: Math.random() * 8 + 8,
delay: Math.random() * 4,
x: Math.random() * 100,
y: Math.random() * 100,
}))
return (
<HeroSection id="home">
<HeroBackground>
<BackgroundElement
style={{
width: "800px",
height: "800px",
background: "linear-gradient(135deg, #264653, #2A9D8F)",
top: "5%",
left: "5%",
transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)`,
}}
/>
<BackgroundElement
style={{
width: "600px",
height: "600px",
background: "linear-gradient(135deg, #2A9D8F,  #2A9D8F)",
top: "50%",
right: "5%",
transform: `translate(${mousePosition.x * -0.015}px, ${mousePosition.y * -0.015}px)`,
}}
/>
<BackgroundElement
style={{
width: "400px",
height: "400px",
background: "linear-gradient(135deg, #2A9D8F, #264653)",
bottom: "10%",
left: "30%",
transform: `translate(${mousePosition.x * 0.01}px, ${mousePosition.y * 0.01}px)`,
}}
/>
</HeroBackground>

<FloatingElements>
    {floatingShapes.map((shape, index) => (
      <FloatingShape
        key={index}
        $size={shape.size}
        $duration={shape.duration}
        $delay={shape.delay}
        style={{
          left: `${shape.x}%`,
          top: `${shape.y}%`,
        }}
      />
    ))}
  </FloatingElements>

  <ResponsiveContainer>
    <HeroContent variants={containerVariants} initial="hidden" animate={controls}>
      <ProfileImageContainer variants={itemVariants}>
        <ProfileImage
          whileHover={{
            scale: 1.05,
            rotate: 2,
            transition: { duration: 0.4 },
          }}
          whileTap={{
            scale: 0.98,
            transition: { duration: 0.2 },
          }}
        >
          <ProfileImageInner>
  <img src="./me.jpg" alt="Profile" />
</ProfileImageInner>

          <StatusIndicator
            animate={{
              scale: [1, 1.1, 1],
              opacity: [1, 0.8, 1],
            }}
            transition={{
              duration: 2,
              repeat: Number.POSITIVE_INFINITY,
              ease: "easeInOut",
            }}
          />
        </ProfileImage>
      </ProfileImageContainer>

      <Greeting variants={itemVariants}>
        <span>Hello!</span>
      </Greeting>

      <HeroTitle variants={itemVariants}>
        <span className="intro">I'm</span>
        <span className="name">Aline NIYONIZERA</span>
      </HeroTitle>

      <HeroSubtitle variants={itemVariants}>
        <span className="typed-text">
          <ReactTyped
            strings={[
              "Backend Developer",
              "Full Stack Developer",
              "Software Engineer",
              "Problem Solver",
              "Tech Innovator",
              "Mobile Developer",
              "Tech Enthusiast",
              "Software Developer",
            ]}
            typeSpeed={60}
            backSpeed={40}
            loop
          />
        </span>
      </HeroSubtitle>

      <HeroDescription variants={itemVariants}>
        A Software developer with 2+ years of experience in software development. Certified Full Stack Developer
        from Klab, currently expanding into mobile development. Passionate about creating innovative solutions and
        solving real-world problems through cutting-edge technology.
      </HeroDescription>

      <CTAButtons variants={itemVariants}>
        <PrimaryButton whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Link to="/contact">
            <FiMail />
            HIRE ME
          </Link>
        </PrimaryButton>
        <SecondaryButton whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <a href="/projects">
            <FiPlay />
            MY WORKS
          </a>
        </SecondaryButton>
      </CTAButtons>

      <SocialLinks variants={itemVariants}>
        <SocialLink
          href="https://github.com/Aline-CROIRE"
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.1, rotate: 15 }}
          whileTap={{ scale: 0.9 }}
        >
          <FiGithub size={28} />
        </SocialLink>
        <SocialLink
          href="mailto:niyocroirealine@gmail.com"
          whileHover={{ scale: 1.1, rotate: -15 }}
          whileTap={{ scale: 0.9 }}
        >
          <FiMail size={28} />
        </SocialLink>
        <SocialLink
          href="https://linkedin.com/in/aline-niyonizera"
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.1, rotate: 15 }}
          whileTap={{ scale: 0.9 }}
        >
          <FiLinkedin size={28} />
        </SocialLink>
      </SocialLinks>

      <StatsContainer variants={itemVariants}>
        <ResponsiveGrid cols={3} mdCols={3} smCols={1} gap="2rem">
          <InView onChange={setIsVisible} delayedCall>
            <StatItem whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} transition={{ duration: 0.3 }}>
              <div className="number">{isVisible && <CountUp end={2} duration={2} suffix="+" />}</div>
              <div className="label">Years Experience</div>
            </StatItem>
          </InView>
          <StatItem whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} transition={{ duration: 0.3 }}>
            <div className="number">{isVisible && <CountUp end={15} duration={2.5} suffix="+" />}</div>
            <div className="label">Projects Completed</div>
          </StatItem>
          <StatItem whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} transition={{ duration: 0.3 }}>
            <div className="number">{isVisible && <CountUp end={95} duration={3} suffix="%" />}</div>
            <div className="label">Client Satisfaction</div>
          </StatItem>
        </ResponsiveGrid>
      </StatsContainer>
    </HeroContent>

    <ScrollIndicator
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 3 }}
      onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
    >
      <span>Scroll Down</span>
      <FiArrowDown size={24} />
    </ScrollIndicator>
  </ResponsiveContainer>
</HeroSection>
)
}
export default EnhancedHero